import React from 'react';
import { motion } from 'framer-motion';
import { Section, FadeIn } from './ui';
import { ClipboardListIcon, PlayCircleIcon, BarChart3Icon } from 'lucide-react';
export function HowItWorks() {
  const steps = [{
    num: '01',
    title: 'Collect the right information.',
    desc: 'Use branded intake forms to understand attendee goals, roles, interests, food needs, wellness preferences, and activity choices before they arrive.',
    icon: ClipboardListIcon,
    color: 'text-brand-teal',
    bg: 'bg-brand-teal/10',
    border: 'border-brand-teal/20'
  }, {
    num: '02',
    title: 'Run a guided event experience.',
    desc: 'Create dynamic agendas, smart networking groups, customized learning pathways tailored to each attendee, wellness options, and real-time reflection moments via the live app.',
    icon: PlayCircleIcon,
    color: 'text-brand-coral',
    bg: 'bg-brand-coral/10',
    border: 'border-brand-coral/20'
  }, {
    num: '03',
    title: 'Deliver a report the client can use.',
    desc: 'Show engagement metrics, survey results, top themes, before/after alignment scores, and concrete commitments in a beautiful post-event report.',
    icon: BarChart3Icon,
    color: 'text-brand-navy',
    bg: 'bg-brand-navy/10',
    border: 'border-brand-navy/20'
  }];
  return <Section id="how-it-works" className="bg-slate-50 border-y border-slate-100 relative overflow-hidden" data-id="element-4413">
      <div className="absolute inset-0 pointer-events-none" data-id="element-4414">
        <div className="absolute top-0 left-1/2 w-px h-full bg-gradient-to-b from-transparent via-slate-200 to-transparent hidden lg:block" data-id="element-4415" />
      </div>

      <div className="max-w-4xl mx-auto text-center mb-20 relative z-10" data-id="element-4416">
        <FadeIn data-id="element-4417">
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-1.5 mb-6 shadow-sm" data-id="element-4418">
            <span className="text-xs font-bold text-brand-navy uppercase tracking-wider" data-id="element-4419">
              The Process
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-brand-navy mb-6 tracking-tight" data-id="element-4420">
            From intake to insights in{' '}
            <span className="text-brand-teal" data-id="element-4421">three steps.</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-4422">
            We've streamlined the entire lifecycle of a group event into a
            single, cohesive platform that feels like magic for both planners
            and attendees.
          </p>
        </FadeIn>
      </div>

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-4423">
        <div className="grid lg:grid-cols-3 gap-8 md:gap-12" data-id="element-4424">
          {steps.map((step, i) => {
          const Icon = step.icon;
          return <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.15,
            duration: 0.5
          }} className="relative" data-id="element-4425">
                {/* Connecting line for mobile/tablet */}
                {i !== steps.length - 1 && <div className="absolute top-24 left-12 w-0.5 h-16 bg-slate-200 lg:hidden" data-id="element-4426" />}

                <div className={`bg-white rounded-[2rem] p-8 shadow-soft border ${step.border} h-full hover:shadow-lg transition-shadow duration-300 relative group`} data-id="element-4427">
                  <div className="absolute -top-6 -right-6 text-8xl font-black text-slate-50 opacity-50 group-hover:scale-110 transition-transform duration-500 pointer-events-none" data-id="element-4428">
                    {step.num}
                  </div>

                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-8 ${step.bg} ${step.color}`} data-id="element-4429">
                    <Icon className="w-8 h-8" data-id="element-4430" />
                  </div>

                  <h3 className="text-2xl font-bold text-brand-navy mb-4 relative z-10" data-id="element-4431">
                    {step.title}
                  </h3>

                  <p className="text-slate-600 leading-relaxed relative z-10" data-id="element-4432">
                    {step.desc}
                  </p>
                </div>
              </motion.div>;
        })}
        </div>
      </div>
    </Section>;
}